import { Injectable } from "@angular/core";
import { Feature, FeatureType } from "@core/models/features/feature.model";
import { Organisation, RoleType } from "@core/models/organisation/organisation.model";
import { canManageQualificationType, isQualificationTypeEnabled } from "@core/models/organisation/qualification.model";
import { BranchService } from "@core/services/branch.service";
import { BehaviorSubject } from "rxjs";

@Injectable({providedIn: 'root'})
export class FeatureService {

  constructor(private branchService:BranchService){}

  // Sections hidden on vanity (branded partner) URLs that are NOT premium-gated. Premium features
  // fall away on their own because the org is treated as non-premium on vanity URLs (see OrganisationAdapter).
  private vanityHiddenFeatures: FeatureType[] = [
    FeatureType.MEMBERS_EMPLOYMENT_INFO,
    FeatureType.TEAMS,
    FeatureType.LEARNINGPLAN_REQUEST_ADD_MEMBERS
  ];

  features$ = new BehaviorSubject<Feature[]|null>(null);
  
  private features: Feature[] = [
    {
      type: FeatureType['WORKSPACE_ACCESS'],
      roles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.MANAGER_READONLY, RoleType.AUDITOR], 
      active: false
    },
    {
      type: FeatureType['DASHBOARD_ACCESS'],
      roles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.MANAGER_READONLY], 
      active: false
    },
    {
      type: FeatureType['ANALYTICS_ACCESS'],
      roles: [RoleType.ADMIN], 
      active: false
    },
    {
      type: FeatureType['MEMBERS_ACCESS'],
      roles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.MANAGER_READONLY, RoleType.AUDITOR],
      active: false
    },
    {
      type: FeatureType['MEMBERS_EXPORT'],
      roles: [RoleType.ADMIN, RoleType.MANAGER],
      active: false
    },
    {
      type: FeatureType['MEMBERS_EDIT'],
      roles: [RoleType.ADMIN, RoleType.MANAGER],
      active: false
    },
    {
      type: FeatureType['MEMBERS_DELETE'],
      roles: [RoleType.ADMIN],
      active: false
    },
    {
      type: FeatureType['MEMBERS_PERSONAL_INFO'],
      roles: [RoleType.ADMIN, RoleType.AUDITOR],
      active: false
    },
    {
      type: FeatureType['MEMBERS_EMPLOYMENT_INFO'],
      roles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.MANAGER_READONLY, RoleType.AUDITOR],
      active: false
    },
    {
      type: FeatureType['MEMBERS_SALARY_INFO'],
      roles: [RoleType.ADMIN],
      active: false
    },
    {
      type: FeatureType['MEMBERS_MANAGE_ADMIN_RIGHTS'],
      roles: [RoleType.ADMIN],
      active: false
    },
    {
      type: FeatureType['MEMBERS_MANAGE_RIGHTS'],
      roles: [RoleType.ADMIN],
      active: false
    },
    {
      type: FeatureType['LEARNINGPLAN_REQUEST_ADD_MEMBERS'],
      roles: [ RoleType.ADMIN, RoleType.MANAGER],
      active: false
    },
    {
      type: FeatureType['MEMBERS_TIMELINE'],
      roles: [ RoleType.ADMIN, RoleType.MANAGER, RoleType.MANAGER_READONLY],
      active: false
    },
    {
      type: FeatureType['TEAMS'],
      roles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.MANAGER_READONLY, RoleType.AUDITOR, RoleType.MEMBER],
      active: false
    },
    {
      type: FeatureType['LEARNINGPLAN_ACCESS'],
      roles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.MANAGER_READONLY],
      active: false
    },
    {
      type: FeatureType['LEARNINGPLAN_REQUESTS_ACCESS'],
      roles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.MANAGER_READONLY],
      active: false
    },
    {
      type: FeatureType['LEARNINGPLAN_BUDGET'],
      roles: [RoleType.ADMIN],
      active: false
    },
    {
      type: FeatureType['LEARNINGPLAN_EXPORT'],
      roles: [RoleType.ADMIN, RoleType.MANAGER],
      active: false
    },
    {
      type: FeatureType['LEARNINGPLAN_EDIT'],
      roles: [RoleType.ADMIN, RoleType.MANAGER],
      active: false
    },
    {
      type: FeatureType['LEARNINGPLAN_COURSE_MEMBERS_ACCESS'],
      roles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.MANAGER_READONLY],
      active: false
    },
    {
      type: FeatureType['LEARNINGPLAN_COURSE_MEMBERS_EDIT'],
      roles: [RoleType.ADMIN, RoleType.MANAGER],
      active: false
    },
    {
      type: FeatureType['LEARNINGPLAN_COURSE_BUDGET_INFO'],
      roles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.MANAGER_READONLY],
      active: false
    },
    {
      type: FeatureType['GRANTS_ACCESS'],
      roles: [RoleType.ADMIN],
      active: false
    },
    {
      type: FeatureType['GRANTS_EDIT'],
      roles: [RoleType.ADMIN],
      active: false
    },

    {
      type: FeatureType['CERTIFICATES_ACCESS'],
      roles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.MANAGER_READONLY, RoleType.AUDITOR],
      active: false
    },
    {
      type: FeatureType['CERTIFICATION_ACCESS'],
      roles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.MANAGER_READONLY, RoleType.AUDITOR],
      active: false
    },
    {
      type: FeatureType['CERTIFICATES_EDIT'],
      roles: [RoleType.ADMIN, RoleType.MANAGER],
      active: false
    },
    {
      type: FeatureType['CERTIFICATIONS_ACCESS'],
      roles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.MANAGER_READONLY, RoleType.AUDITOR],
      active: false
    },
    {
      type: FeatureType['CERTIFICATIONS_EDIT'],
      roles: [RoleType.ADMIN, RoleType.MANAGER],
      active: false
    },
    {
      type: FeatureType['QUALIFICATIONS_ACCESS'],
      roles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.MANAGER_READONLY, RoleType.AUDITOR],
      active: false
    },
    {
      type: FeatureType['QUALIFICATIONS_EDIT'],
      roles: [RoleType.ADMIN, RoleType.MANAGER],
      active: false
    },
    {
      type: FeatureType['QUALIFICATIONS_SECTION_ACCESS'],
      roles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.MANAGER_READONLY, RoleType.AUDITOR],
      active: false
    },
    {
      type: FeatureType['QUALIFICATIONS_SECTION_EDIT'],
      roles: [RoleType.ADMIN, RoleType.MANAGER],
      active: false
    },
    {
      type: FeatureType['ENROLMENTS_ACCESS'],
      roles: [RoleType.ADMIN],
      active: false
    },
    {
      type: FeatureType['CONNECTIONS_ACCESS'],
      roles: [RoleType.ADMIN],
      active: false
    },
    {
      type: FeatureType['CONNECTIONS_EDIT'],
      roles: [RoleType.ADMIN],
      active: false
    },
    {
      type: FeatureType['FORMS_ACCESS'],
      roles: [RoleType.ADMIN],
      active: false
    },
    {
      type: FeatureType['FORMS_SECTION_ACCESS'],
      roles: [RoleType.ADMIN],
      active: false
    },
    {
      type: FeatureType['LEARNINGPLAN_COURSE_FORMS_ACCESS'],
      roles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.MANAGER_READONLY],
      active: false
    },
    {
      type: FeatureType['LEARNINGPLAN_COURSE_FORMS_EDIT'],
      roles: [RoleType.ADMIN, RoleType.MANAGER],
      active: false
    },

    {
      type: FeatureType['SETTINGS_ACCESS'],
      roles: [RoleType.ADMIN],
      active: false
    },
    {
      type: FeatureType['FLA_ACCESS'],
      roles: [RoleType.ADMIN],
      active: false
    },
    {
      type: FeatureType['FOD_WASO_ACCESS'],
      roles: [RoleType.ADMIN],
      active: false
    },
     {
      type: FeatureType['ATTACHMENTS_ACCESS'],
      roles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.MANAGER_READONLY],
      active: false
    },
    {
      type: FeatureType['ATTACHMENTS_EDIT'],
      roles: [RoleType.ADMIN, RoleType.MANAGER, RoleType.MANAGER_READONLY],
      active: false
    },
    {
      type: FeatureType['CV_EMAIL_PDF'],
      roles: [RoleType.ADMIN, RoleType.MANAGER],
      active: false
    }
  ];


  getFeatures(organisation:Organisation){
    const features = [...this.features];
    features.forEach(f =>{
      f.active = false;
      if(f.roles.find(x => x == organisation.role)){
        f.active = true;
      }

      if(f.type == FeatureType.MEMBERS_MANAGE_ADMIN_RIGHTS){
        f.active = organisation.permissions.canManageAdministrators;
      }

      if(f.type == FeatureType.MEMBERS_SALARY_INFO){
        f.active = organisation.permissions.canManageWages;
      }

      if(!f.active && f.type == FeatureType.LEARNINGPLAN_REQUEST_ADD_MEMBERS){
        f.active = organisation.permissions.canAddColleaguesToRequest;
      }

      // On unless it was taken away: the API leaves the right out for members it never set it on.
      if(f.type == FeatureType.LEARNINGPLAN_REQUESTS_ACCESS){
        f.active = f.active && organisation.permissions.canAccessTrainingRequests !== false;
      }

      if(f.type == FeatureType.ATTACHMENTS_ACCESS){
        f.active = organisation.isPremium && organisation.attachmentModuleEnabled && organisation.permissions.canManageAttachments;
      }

      // Reading and writing certificates are both the same right; without it there is nothing to
      // add one from, on the certificate screens or on the qualification ones.
      if(f.type == FeatureType.CERTIFICATES_ACCESS){
        f.active = organisation.isPremium && organisation.permissions.canManageCertificates;
      }

      if(f.type == FeatureType.CERTIFICATES_EDIT){
        f.active = f.active && organisation.isPremium && organisation.permissions.canManageCertificates;
      }

      // The qualification catalogue, per type: the module has to be on, and the member has to be
      // allowed it. Managing implies reading, as the API reports it.
      if(f.type == FeatureType.CERTIFICATIONS_ACCESS){
        f.active = f.active && isQualificationTypeEnabled(organisation, 'certification');
      }
      if(f.type == FeatureType.CERTIFICATIONS_EDIT){
        f.active = f.active && canManageQualificationType(organisation, 'certification');
      }
      if(f.type == FeatureType.QUALIFICATIONS_ACCESS){
        f.active = f.active && (isQualificationTypeEnabled(organisation, 'knowledge') || isQualificationTypeEnabled(organisation, 'skill'));
      }
      if(f.type == FeatureType.QUALIFICATIONS_EDIT){
        f.active = f.active && (canManageQualificationType(organisation, 'knowledge') || canManageQualificationType(organisation, 'skill'));
      }
      // The section itself, by role only: without a module its pages offer the module instead of
      // the matrix. Deliberately not ANDed with a module, or the upsell would hide from exactly
      // the organisations it is for.
      if(f.type == FeatureType.QUALIFICATIONS_SECTION_ACCESS || f.type == FeatureType.QUALIFICATIONS_SECTION_EDIT){
        f.active = f.active && organisation.isPremium;
      }

      if(f.type == FeatureType.DASHBOARD_ACCESS && !organisation.isPremium){
        f.active = false;
      }

      if(f.type == FeatureType.CV_EMAIL_PDF){
        f.active = organisation.features.find(x => x.name == 'cv_mailing')?.enabled ?? false;
      }
      
      if(f.type == FeatureType.CERTIFICATION_ACCESS){
        f.active = organisation.features.find(x => x.name == 'certification')?.enabled ?? false;
      }
      if((f.type == FeatureType.FORMS_ACCESS || f.type == FeatureType.LEARNINGPLAN_COURSE_FORMS_ACCESS || f.type == FeatureType.LEARNINGPLAN_COURSE_FORMS_EDIT) && f.active){
        f.active = organisation.features.find(x => x.name == 'form_builder')?.enabled ?? false;
      }

      //deprecated
      if(f.type == FeatureType.FLA_ACCESS){
        f.active = false;
      }
    })

    if(this.branchService.vanityMode){
      features.forEach(f => {
        if(this.vanityHiddenFeatures.includes(f.type)){
          f.active = false;
        }
      })
    }

    this.features$.next(features);
  }

  checkRoleFeatureAcces(role:RoleType, type:FeatureType){
    const feature = this.features.find(x => x.type == type);
    return feature ? feature.roles.some(x => x == role) : false;
  }

  hasFeature(type: FeatureType){
    const features = this.features$.getValue();
    if(features){
      const feature = features.find(f => f.type === type && f.active);
      if (!feature) return false;
      return true;
    }
    
    return false;
  }

  clear(){
    this.features$.next(null);
  }
}
